import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getApiEndpoint } from '../../../config/api';
import { FaArrowLeft, FaTicketAlt, FaPaperclip, FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';
import MainHeader from '../../shared/MainHeader/MainHeader';
import './Support.css';

const CATEGORIES = [
  { value: 'general', label: 'General Enquiry', desc: 'General questions or feedback' },
  { value: 'bug_report', label: 'Bug Report', desc: 'Something isn\'t working correctly' },
  { value: 'feature_request', label: 'Feature Request', desc: 'Suggest a new feature or improvement' },
  { value: 'access_issue', label: 'Access Issue', desc: 'Can\'t access something you should be able to' },
  { value: 'billing', label: 'Billing', desc: 'Questions about your account or billing' },
  { value: 'compliance', label: 'Compliance', desc: 'Compliance or regulatory questions' },
  { value: 'data_request', label: 'Data Request', desc: 'Request data export or changes' },
  { value: 'onboarding', label: 'Onboarding', desc: 'Help setting up properties or users' },
  { value: 'other', label: 'Other', desc: 'Anything else' },
];

const PRIORITIES = [
  { value: 'low', label: 'Low', desc: 'No urgency — general question', color: '#64748b' },
  { value: 'medium', label: 'Medium', desc: 'Normal priority — within a few days', color: '#1A80E5' },
  { value: 'high', label: 'High', desc: 'Important — needs attention soon', color: '#f59e0b' },
  { value: 'urgent', label: 'Urgent', desc: 'Critical — blocking your work', color: '#ef4444' },
];

const SupportTicketCreate = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    subject: '', description: '', category: 'general', priority: 'medium', property_id: ''
  });
  const [files, setFiles] = useState([]);
  const [properties, setProperties] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const res = await fetch(getApiEndpoint('/api/properties'), {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (res.ok) {
          const data = await res.json();
          setProperties(Array.isArray(data) ? data : data.properties || []);
        }
      } catch (e) { /* non-critical */ }
    };
    fetchProperties();
  }, []);

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    if (files.length + newFiles.length > 5) {
      toast.error('Maximum 5 attachments allowed');
      return;
    }
    setFiles(prev => [...prev, ...newFiles]);
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.subject.trim() || !form.description.trim()) {
      toast.error('Subject and description are required');
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('subject', form.subject.trim());
      formData.append('description', form.description.trim());
      formData.append('category', form.category);
      formData.append('priority', form.priority);
      if (form.property_id) formData.append('property_id', form.property_id);
      files.forEach(f => formData.append('attachments', f));

      const res = await fetch(getApiEndpoint('/api/support/tickets'), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: formData
      });

      if (res.ok) {
        const ticket = await res.json();
        toast.success(`Ticket ${ticket.ticket_ref} created`);
        navigate(`/support/tickets/${ticket.id}`);
      } else {
        const err = await res.json();
        toast.error(err.message || 'Failed to create ticket');
      }
    } catch (error) {
      toast.error('Failed to create ticket');
    } finally {
      setSubmitting(false);
    }
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  return (
    <div className="sup-page">
      <MainHeader />
      <div className="sup-container">
        <div className="sup-header">
          <div className="sup-header-left">
            <button className="sup-btn sup-btn-ghost" onClick={() => navigate('/support/tickets')}>
            <FaArrowLeft /> Back
          </button>
        </div>
      </div>

      <div className="sup-create-card">
        <div className="sup-create-header">
          <FaTicketAlt className="sup-create-icon" />
          <div>
            <h2>New Support Ticket</h2>
            <p>Describe your issue and we'll get back to you as soon as possible.</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="sup-form">
          {/* Subject */}
          <div className="sup-field">
            <label className="sup-label">Subject <span className="sup-required">*</span></label>
            <input
              type="text"
              className="sup-input"
              placeholder="Brief summary of your issue..."
              value={form.subject}
              onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
              maxLength={255}
            />
          </div>

          {/* Category */}
          <div className="sup-field">
            <label className="sup-label">Category</label>
            <div className="sup-category-grid">
              {CATEGORIES.map(c => (
                <button
                  key={c.value}
                  type="button"
                  className={`sup-category-card ${form.category === c.value ? 'active' : ''}`}
                  onClick={() => setForm(f => ({ ...f, category: c.value }))}
                >
                  <span className="sup-category-name">{c.label}</span>
                  <span className="sup-category-desc">{c.desc}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Priority */}
          <div className="sup-field">
            <label className="sup-label">Priority</label>
            <div className="sup-priority-row">
              {PRIORITIES.map(p => (
                <button
                  key={p.value}
                  type="button"
                  className={`sup-priority-card ${form.priority === p.value ? 'active' : ''}`}
                  style={{ '--priority-color': p.color }}
                  onClick={() => setForm(f => ({ ...f, priority: p.value }))}
                >
                  <span className="sup-priority-dot" />
                  <span className="sup-priority-name">{p.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Related Property */}
          {properties.length > 0 && (
            <div className="sup-field">
              <label className="sup-label">Related Property <span className="sup-optional">(optional)</span></label>
              <select
                className="sup-input"
                value={form.property_id}
                onChange={e => setForm(f => ({ ...f, property_id: e.target.value }))}
              >
                <option value="">— None —</option>
                {properties.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Description */}
          <div className="sup-field">
            <label className="sup-label">Description <span className="sup-required">*</span></label>
            <textarea
              className="sup-textarea"
              placeholder="Describe your issue in detail. Include steps to reproduce if reporting a bug..."
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              rows={6}
            />
          </div>

          {/* Attachments */}
          <div className="sup-field">
            <label className="sup-label">Attachments <span className="sup-optional">(optional, max 5)</span></label>
            <div className="sup-file-zone">
              <label className="sup-file-label">
                <FaPaperclip /> Choose files or drag & drop
                <input type="file" multiple onChange={handleFileChange} className="sup-file-input" />
              </label>
            </div>
            {files.length > 0 && (
              <div className="sup-file-list">
                {files.map((f, i) => (
                  <div key={i} className="sup-file-item">
                    <span className="sup-file-name">{f.name}</span>
                    <span className="sup-file-size">{formatSize(f.size)}</span>
                    <button type="button" className="sup-file-remove" onClick={() => removeFile(i)}>
                      <FaTimes />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Submit */}
          <div className="sup-form-actions">
            <button type="button" className="sup-btn sup-btn-ghost" onClick={() => navigate('/support/tickets')}>
              Cancel
            </button>
            <button type="submit" className="sup-btn sup-btn-primary" disabled={submitting}>
              {submitting ? 'Submitting...' : 'Submit Ticket'}
            </button>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
};

export default SupportTicketCreate;
